import { useEffect, useState, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { api, formatApiErrorDetail, GOLD, AREA_PRESETS, PKG_STATUSES, COUNT_FIELDS, computePace, IMPORT_FIELDS } from "@/lib/apiClient";
import { Layout } from "@/components/Layout";
import { ProgressBar } from "@/components/ProgressBar";
import { JobStatusBadge, PkgStatusBadge } from "@/components/Badges";
import { toast } from "sonner";
import { ArrowLeft, Plus, Share2, Trash2, Loader2, Copy, Check, Pencil, FileText, DollarSign, LayoutGrid, ExternalLink, Upload } from "lucide-react";
import { LineChart, Line, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from "recharts";

const field = "w-full rounded-lg border border-white/15 bg-[#141414] px-3 py-2 text-sm text-[#f7f3ec] outline-none transition-colors placeholder:text-neutral-600 focus:border-[#c5a24a] focus:ring-1 focus:ring-[#c5a24a]";
const lbl = "mb-1.5 block text-xs font-semibold uppercase tracking-wide text-neutral-400";
const btnGold = "gold-gradient flex items-center gap-2 rounded-lg px-4 py-2 text-sm font-semibold text-[#121212] transition-opacity hover:opacity-90 disabled:opacity-60";
const btnGhost = "rounded-lg border border-white/15 px-4 py-2 text-sm font-medium text-neutral-300 transition-colors hover:bg-white/5";
const money = (n) => "$" + (n || 0).toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 0 });

export default function ProjectDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [p, setP] = useState(null);
  const [areas, setAreas] = useState([]);
  const [packages, setPackages] = useState([]);
  const [time, setTime] = useState([]);
  const [snaps, setSnaps] = useState([]);
  const [published, setPublished] = useState([]);
  const [tab, setTab] = useState("tracker");
  const [modal, setModal] = useState(null); // {type, data}

  const load = useCallback(() => {
    api.get(`/projects/${id}`).then((r) => setP(r.data)).catch(() => navigate("/dashboard"));
    api.get(`/projects/${id}/areas`).then((r) => setAreas(r.data));
    api.get(`/projects/${id}/packages`).then((r) => setPackages(r.data));
    api.get(`/projects/${id}/time`).then((r) => setTime(r.data));
    api.get(`/projects/${id}/snapshots`).then((r) => setSnaps(r.data));
    api.get(`/projects/${id}/reports`).then((r) => setPublished(r.data));
  }, [id, navigate]);
  useEffect(() => { load(); }, [load]);
  const reload = () => load();

  if (!p) return <Layout><div className="flex justify-center py-32 text-neutral-500"><Loader2 className="h-6 w-6 animate-spin" /></div></Layout>;

  const pkgOf = (aid) => packages.filter((x) => x.area_id === aid);
  const chart = snaps.map((s) => ({ week: s.week_key.includes("-W") ? `W${s.week_key.split("-W")[1]}` : s.week_key, progress: s.overall_percent }));
  const pace = computePace(snaps);

  const delPackage = async (pkid) => { await api.delete(`/packages/${pkid}`); reload(); };
  const delArea = async (aid) => { if (window.confirm("Delete this area and its packages?")) { await api.delete(`/areas/${aid}`); reload(); } };
  const delTime = async (tid) => { await api.delete(`/time/${tid}`); reload(); };

  const addRiskNote = async () => {
    const note = p.forecast?.risk_note;
    if (!note) return;
    const existing = (p.report?.risk_notes) || [];
    if (existing.includes(note)) { toast.info("Already in report risk notes"); return; }
    try {
      await api.put(`/projects/${id}`, { report: { ...(p.report || {}), risk_notes: [...existing, note] } });
      toast.success("Added to report risk notes");
      reload();
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
  };

  const TABS = [
    { k: "tracker", label: "Tracker", icon: LayoutGrid },
    { k: "costs", label: "Costs & Budget", icon: DollarSign },
    { k: "report", label: "Report & Share", icon: FileText },
  ];

  return (
    <Layout>
      <div className="border-b border-white/10 bg-[#141414]/70 px-8 py-6 backdrop-blur-xl">
        <button onClick={() => navigate("/dashboard")} data-testid="back-to-jobs" className="mb-4 flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wide text-neutral-500 transition-colors hover:text-[#c5a24a]"><ArrowLeft className="h-3.5 w-3.5" /> All Projects</button>
        <div className="flex flex-wrap items-start justify-between gap-4">
          <div>
            <div className="flex items-center gap-3">
              <h1 className="font-heading text-3xl font-bold tracking-tight text-[#f7f3ec]">{p.name}</h1>
              <JobStatusBadge status={p.status} />
            </div>
            <div className="mt-2 flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-neutral-500">
              <span className="font-semibold text-neutral-400">{p.job_number}</span>
              <span className="text-neutral-300">{p.client_name}</span>
              {p.project_manager && <span>PM: {p.project_manager}</span>}
              {p.scope_total ? <span>Scope: {p.scope_total} dwgs</span> : null}
            </div>
          </div>
          <div className="flex items-center gap-4">
            <div className="text-right">
              <div className="font-heading text-4xl font-bold tabular-nums text-[#c5a24a]">{p.stats.overall_percent}%</div>
              <div className="text-[10px] uppercase tracking-widest text-neutral-500">Complete</div>
            </div>
            <div className="flex gap-2">
              <button onClick={() => setModal({ type: "editProject" })} data-testid="edit-job-btn" className={btnGhost + " flex items-center gap-2"}><Pencil className="h-4 w-4" /> Edit</button>
              <button onClick={() => setModal({ type: "share" })} data-testid="share-btn" className="flex items-center gap-2 rounded-lg border border-[#c5a24a]/50 bg-[#c5a24a]/10 px-3 py-2 text-sm font-semibold text-[#c5a24a] transition-colors hover:bg-[#c5a24a]/20"><Share2 className="h-4 w-4" /> Share</button>
            </div>
          </div>
        </div>
        <div className="mt-5 flex gap-1">
          {TABS.map((t) => (
            <button key={t.k} data-testid={`tab-${t.k}`} onClick={() => setTab(t.k)} className="flex items-center gap-2 rounded-lg px-4 py-2 text-sm font-semibold transition-colors" style={tab === t.k ? { backgroundColor: "rgba(197,162,74,0.15)", color: "#d8b968" } : { color: "#a3a3a3" }}>
              <t.icon className="h-4 w-4" /> {t.label}
            </button>
          ))}
        </div>
      </div>

      <div className="p-8">
        {tab === "tracker" && (
          <div className="space-y-6">
            <div className="flex justify-end gap-2">
              <button onClick={() => setModal({ type: "importDrawings" })} data-testid="import-drawings-btn" className={btnGhost + " flex items-center gap-2"}><Upload className="h-4 w-4" /> Import Drawing List</button>
              <button onClick={() => setModal({ type: "area" })} data-testid="add-area-btn" className={btnGold}><Plus className="h-4 w-4" strokeWidth={2.5} /> Add Area</button>
            </div>
            {areas.length === 0 && <div className="rounded-xl border border-white/10 bg-[#1a1a1a] py-14 text-center text-sm text-neutral-500" data-testid="areas-empty">No areas yet. Add one (e.g. Block Valve, LACT).</div>}
            {areas.map((a) => {
              const st = p.stats.by_area[a.id] || { totals: {}, percent: 0 };
              const pkgs = pkgOf(a.id);
              return (
                <div key={a.id} className="overflow-hidden rounded-xl border border-white/10 bg-[#1a1a1a]" data-testid={`area-${a.id}`}>
                  <div className="flex flex-wrap items-center justify-between gap-3 border-b border-white/10 bg-white/[0.03] px-5 py-3">
                    <div className="flex items-center gap-3">
                      <h3 className="font-heading text-lg font-bold text-[#f7f3ec]">{a.name}</h3>
                      <span className="text-xs text-neutral-500">Target {a.target_drawings || 0} · Received {a.received_date || "TBA"}</span>
                      <span className="rounded-full bg-[#c5a24a]/15 px-2 py-0.5 text-xs font-semibold text-[#c5a24a]">{st.percent || 0}%</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <button onClick={() => setModal({ type: "package", areaId: a.id })} data-testid={`add-package-${a.id}`} className="flex items-center gap-1.5 rounded-lg border border-white/15 px-2.5 py-1.5 text-xs font-semibold text-neutral-300 hover:bg-white/5"><Plus className="h-3.5 w-3.5" /> Package</button>
                      <button onClick={() => setModal({ type: "area", data: a })} className="rounded-md p-1.5 text-neutral-500 hover:bg-white/5 hover:text-neutral-200"><Pencil className="h-3.5 w-3.5" /></button>
                      <button onClick={() => delArea(a.id)} className="rounded-md p-1.5 text-neutral-500 hover:bg-red-950/50 hover:text-red-400"><Trash2 className="h-3.5 w-3.5" /></button>
                    </div>
                  </div>
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b border-white/10 text-[11px] uppercase tracking-wider text-neutral-500">
                        <th className="px-4 py-2 text-left font-semibold">Drawing #</th>
                        {COUNT_FIELDS.map((c) => <th key={c.key} className="px-2 py-2 text-center font-semibold">{c.label}</th>)}
                        <th className="px-2 py-2 text-center font-semibold">%</th>
                        <th className="px-3 py-2 text-left font-semibold">Status</th>
                        <th className="px-2 py-2" />
                      </tr>
                    </thead>
                    <tbody>
                      {pkgs.length === 0 && <tr><td colSpan={10} className="px-4 py-5 text-center text-xs text-neutral-600">No packages in this area.</td></tr>}
                      {pkgs.map((pk) => {
                        const pct = pk.received ? Math.round(100 * (pk.completed || 0) / pk.received) : 0;
                        return (
                          <tr key={pk.id} data-testid={`package-${pk.id}`} className="border-b border-white/5 last:border-0 hover:bg-white/[0.02]">
                            <td className="px-4 py-2.5">
                              <div className="font-semibold text-[#f7f3ec]">{pk.drawing_number}</div>
                              {pk.description && <div className="max-w-[240px] truncate text-[11px] text-neutral-500" title={pk.description}>{pk.description}</div>}
                            </td>
                            {COUNT_FIELDS.map((c) => <td key={c.key} className={`px-2 py-2.5 text-center tabular-nums ${c.key === "requires_attention" && pk[c.key] ? "text-red-400" : "text-neutral-300"}`}>{pk[c.key] || 0}</td>)}
                            <td className="px-2 py-2.5 text-center font-semibold tabular-nums text-[#c5a24a]">{pct}%</td>
                            <td className="px-3 py-2.5"><PkgStatusBadge status={pk.status} /></td>
                            <td className="px-2 py-2.5">
                              <div className="flex justify-end gap-1">
                                <button onClick={() => setModal({ type: "package", areaId: a.id, data: pk })} data-testid={`edit-package-${pk.id}`} className="rounded-md p-1.5 text-neutral-500 hover:bg-white/5 hover:text-neutral-200"><Pencil className="h-3.5 w-3.5" /></button>
                                <button onClick={() => delPackage(pk.id)} className="rounded-md p-1.5 text-neutral-500 hover:bg-red-950/50 hover:text-red-400"><Trash2 className="h-3.5 w-3.5" /></button>
                              </div>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                    {pkgs.length > 0 && (
                      <tfoot>
                        <tr className="bg-white/[0.03] text-xs font-semibold text-neutral-300">
                          <td className="px-4 py-2">Totals</td>
                          {COUNT_FIELDS.map((c) => <td key={c.key} className="px-2 py-2 text-center tabular-nums">{st.totals[c.key] || 0}</td>)}
                          <td className="px-2 py-2 text-center text-[#c5a24a]">{st.percent || 0}%</td>
                          <td colSpan={2} />
                        </tr>
                      </tfoot>
                    )}
                  </table>
                </div>
              );
            })}
          </div>
        )}

        {tab === "costs" && <CostsTab project={p} areas={areas} time={time} onAdd={() => setModal({ type: "time" })} onDel={delTime} onAddRisk={addRiskNote} />}

        {tab === "report" && <ReportTab project={p} chart={chart} pace={pace} published={published} onEditNarrative={() => setModal({ type: "narrative" })} onPublish={() => setModal({ type: "publish" })} projectId={id} navigate={navigate} />}
      </div>

      {modal?.type === "area" && <AreaModal projectId={id} area={modal.data} onClose={() => setModal(null)} onSaved={() => { setModal(null); reload(); }} />}
      {modal?.type === "package" && <PackageModal projectId={id} areaId={modal.areaId} pkg={modal.data} onClose={() => setModal(null)} onSaved={() => { setModal(null); reload(); }} />}
      {modal?.type === "time" && <TimeModal projectId={id} areas={areas} team={p.team} onClose={() => setModal(null)} onSaved={() => { setModal(null); reload(); }} />}
      {modal?.type === "editProject" && <ProjectModal project={p} onClose={() => setModal(null)} onSaved={() => { setModal(null); reload(); }} />}
      {modal?.type === "narrative" && <NarrativeModal project={p} onClose={() => setModal(null)} onSaved={() => { setModal(null); reload(); }} />}
      {modal?.type === "publish" && <PublishModal project={p} onClose={() => setModal(null)} onSaved={() => { setModal(null); reload(); }} />}
      {modal?.type === "share" && <ShareModal project={p} onClose={() => setModal(null)} onSaved={reload} />}
      {modal?.type === "importDrawings" && <ImportDrawingsModal projectId={id} areas={areas} onClose={() => setModal(null)} onSaved={() => { setModal(null); reload(); }} />}
    </Layout>
  );
}

function Modal({ title, children, testid, wide }) {
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 p-4 backdrop-blur-sm">
      <div className={`w-full ${wide ? "max-w-2xl" : "max-w-lg"} max-h-[90vh] overflow-y-auto rounded-xl border border-white/10 bg-[#1a1a1a] shadow-2xl`} data-testid={testid}>
        <div className="sticky top-0 border-b border-white/10 bg-[#1a1a1a] px-6 py-4"><h3 className="font-heading text-xl font-bold text-[#f7f3ec]">{title}</h3></div>
        {children}
      </div>
    </div>
  );
}

function AreaModal({ projectId, area, onClose, onSaved }) {
  const [f, setF] = useState(area || { name: "", target_drawings: "", received_date: "", budget: "" });
  const [saving, setSaving] = useState(false);
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });
  const submit = async (e) => {
    e.preventDefault(); setSaving(true);
    const payload = { name: f.name, received_date: f.received_date || "", target_drawings: parseInt(f.target_drawings) || 0, budget: parseFloat(f.budget) || 0 };
    try {
      if (area) await api.put(`/areas/${area.id}`, payload); else await api.post(`/projects/${projectId}/areas`, payload);
      toast.success("Saved"); onSaved();
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); } finally { setSaving(false); }
  };
  return (
    <Modal title={area ? "Edit Area" : "Add Area"} testid="area-modal">
      <form onSubmit={submit} className="space-y-4 px-6 py-5">
        <div>
          <label className={lbl}>Area Name</label>
          <input data-testid="area-name-input" required list="area-presets" className={field} value={f.name} onChange={set("name")} placeholder="Block Valve" />
          <datalist id="area-presets">{AREA_PRESETS.map((a) => <option key={a} value={a} />)}</datalist>
        </div>
        <div className="grid grid-cols-3 gap-4">
          <div><label className={lbl}>Target Dwgs</label><input type="number" className={field} value={f.target_drawings} onChange={set("target_drawings")} placeholder="951" /></div>
          <div><label className={lbl}>Received Date</label><input className={field} value={f.received_date} onChange={set("received_date")} placeholder="Oct 20th" /></div>
          <div><label className={lbl}>Budget ($)</label><input type="number" className={field} value={f.budget} onChange={set("budget")} placeholder="42434" /></div>
        </div>
        <div className="flex justify-end gap-3 pt-2">
          <button type="button" onClick={onClose} className={btnGhost}>Cancel</button>
          <button data-testid="area-save-btn" disabled={saving} type="submit" className={btnGold}>{saving && <Loader2 className="h-4 w-4 animate-spin" />} Save</button>
        </div>
      </form>
    </Modal>
  );
}

function PackageModal({ projectId, areaId, pkg, onClose, onSaved }) {
  const [f, setF] = useState(pkg || { drawing_number: "", description: "", received_date: "", received: 0, working_on: 0, checking: 0, completed: 0, requires_attention: 0, returned: 0, status: "TBA", notes: "" });
  const [saving, setSaving] = useState(false);
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });
  const num = (k) => (e) => setF({ ...f, [k]: parseInt(e.target.value) || 0 });
  const submit = async (e) => {
    e.preventDefault(); setSaving(true);
    const payload = { ...f, area_id: areaId, received: +f.received || 0, working_on: +f.working_on || 0, checking: +f.checking || 0, completed: +f.completed || 0, requires_attention: +f.requires_attention || 0, returned: +f.returned || 0 };
    try {
      if (pkg) await api.put(`/packages/${pkg.id}`, payload); else await api.post(`/projects/${projectId}/packages`, payload);
      toast.success("Saved"); onSaved();
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); } finally { setSaving(false); }
  };
  return (
    <Modal title={pkg ? "Edit Package" : "Add Package"} testid="package-modal">
      <form onSubmit={submit} className="space-y-4 px-6 py-5">
        <div className="grid grid-cols-2 gap-4">
          <div><label className={lbl}>Drawing #</label><input data-testid="package-number-input" required className={field} value={f.drawing_number} onChange={set("drawing_number")} placeholder="TR-4204" /></div>
          <div><label className={lbl}>Received Date</label><input className={field} value={f.received_date} onChange={set("received_date")} placeholder="20-Oct" /></div>
        </div>
        <div><label className={lbl}>Description</label><input className={field} value={f.description} onChange={set("description")} placeholder="BV02, 04, 05, 08..." /></div>
        <div className="grid grid-cols-3 gap-3">
          {COUNT_FIELDS.map((c) => (
            <div key={c.key}><label className={lbl}>{c.label}</label><input data-testid={`package-${c.key}-input`} type="number" className={field} value={f[c.key]} onChange={num(c.key)} /></div>
          ))}
        </div>
        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className={lbl}>Status</label>
            <select data-testid="package-status-input" className={field} value={f.status} onChange={set("status")}>{PKG_STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}</select>
          </div>
          <div><label className={lbl}>Notes</label><input className={field} value={f.notes} onChange={set("notes")} /></div>
        </div>
        <div className="flex justify-end gap-3 pt-2">
          <button type="button" onClick={onClose} className={btnGhost}>Cancel</button>
          <button data-testid="package-save-btn" disabled={saving} type="submit" className={btnGold}>{saving && <Loader2 className="h-4 w-4 animate-spin" />} Save</button>
        </div>
      </form>
    </Modal>
  );
}

function CostsTab({ project, areas, time, onAdd, onDel, onAddRisk }) {
  const c = project.costs || { rows: [], total_budget: 0, total_actual: 0, total_variance: 0, off_track: false };
  const fc = project.forecast || {};
  const areaName = (aid) => areas.find((a) => a.id === aid)?.name || "Unassigned";
  return (
    <div className="space-y-6">
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
        <div className="rounded-xl border border-white/10 bg-[#1a1a1a] p-5"><div className="text-xs font-semibold uppercase tracking-widest text-neutral-500">Total Budget</div><div className="mt-2 font-heading text-3xl font-bold tabular-nums text-[#f7f3ec]">{money(c.total_budget)}</div></div>
        <div className="rounded-xl border border-white/10 bg-[#1a1a1a] p-5"><div className="text-xs font-semibold uppercase tracking-widest text-neutral-500">Actual (Hours × Rate)</div><div className="mt-2 font-heading text-3xl font-bold tabular-nums text-[#c5a24a]">{money(c.total_actual)}</div></div>
        <div className={`rounded-xl border p-5 ${c.off_track ? "border-red-500/40 bg-red-950/20" : "border-white/10 bg-[#1a1a1a]"}`}><div className="text-xs font-semibold uppercase tracking-widest text-neutral-500">Variance</div><div className={`mt-2 font-heading text-3xl font-bold tabular-nums ${c.total_variance < 0 ? "text-red-400" : "text-green-400"}`}>{money(c.total_variance)}</div><div className="mt-1 text-[11px] font-semibold uppercase tracking-wide">{c.off_track ? <span className="text-red-400">Off track — over budget</span> : <span className="text-green-400">On track</span>}</div></div>
      </div>

      {/* Budget Forecast */}
      <div data-testid="forecast-card" className={`rounded-xl border p-5 ${fc.over_budget ? "border-red-500/50 bg-red-950/20" : "border-[#c5a24a]/30 bg-[#c5a24a]/[0.06]"}`}>
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-widest text-neutral-400">
            {fc.over_budget ? <span className="text-red-400">⚠ Budget Forecast — Projected Overrun</span> : <span className="text-[#c5a24a]">Budget Forecast</span>}
          </div>
          {fc.risk_note && <button onClick={onAddRisk} data-testid="add-risk-btn" className="rounded-lg border border-white/15 px-2.5 py-1 text-xs font-semibold text-neutral-300 hover:bg-white/5">Add to Report Risk Notes</button>}
        </div>
        <div className="mt-4 grid grid-cols-2 gap-4 sm:grid-cols-4">
          <div><div className="text-[11px] uppercase tracking-wide text-neutral-500">Pace</div><div className="font-heading text-2xl font-bold tabular-nums text-[#f7f3ec]">{fc.velocity_per_week ?? 0}<span className="text-sm text-neutral-500">/wk</span></div></div>
          <div><div className="text-[11px] uppercase tracking-wide text-neutral-500">Remaining</div><div className="font-heading text-2xl font-bold tabular-nums text-[#f7f3ec]">{fc.remaining ?? 0}</div></div>
          <div><div className="text-[11px] uppercase tracking-wide text-neutral-500">Est. Weeks Left</div><div className="font-heading text-2xl font-bold tabular-nums text-[#f7f3ec]">{fc.weeks_remaining ?? "—"}</div></div>
          <div><div className="text-[11px] uppercase tracking-wide text-neutral-500">Projected Cost</div><div className={`font-heading text-2xl font-bold tabular-nums ${fc.over_budget ? "text-red-400" : "text-green-400"}`}>{money(fc.projected_total_cost)}</div></div>
        </div>
        {fc.risk_note && <p className={`mt-4 rounded-lg px-3 py-2 text-sm ${fc.over_budget ? "bg-red-950/40 text-red-200" : "bg-white/5 text-neutral-300"}`}>{fc.risk_note}</p>}
      </div>

      <div className="overflow-hidden rounded-xl border border-white/10 bg-[#1a1a1a]">
        <div className="border-b border-white/10 px-5 py-3 text-sm font-semibold text-[#f7f3ec]">Budget vs Actual by Area</div>
        <table className="w-full text-sm">
          <thead><tr className="border-b border-white/10 text-[11px] uppercase tracking-wider text-neutral-500"><th className="px-5 py-2 text-left font-semibold">Area</th><th className="px-3 py-2 text-right font-semibold">Budget</th><th className="px-3 py-2 text-right font-semibold">Actual</th><th className="px-3 py-2 text-right font-semibold">Variance</th><th className="px-3 py-2 text-right font-semibold">% Used</th></tr></thead>
          <tbody>
            {c.rows.length === 0 && <tr><td colSpan={5} className="px-5 py-5 text-center text-xs text-neutral-600">Add areas with budgets to track cost.</td></tr>}
            {c.rows.map((r) => (
              <tr key={r.area_id} className="border-b border-white/5 last:border-0">
                <td className="px-5 py-2.5 text-neutral-200">{r.area_name}</td>
                <td className="px-3 py-2.5 text-right tabular-nums text-neutral-300">{money(r.budget)}</td>
                <td className="px-3 py-2.5 text-right tabular-nums text-neutral-300">{money(r.actual)}</td>
                <td className={`px-3 py-2.5 text-right tabular-nums ${r.variance < 0 ? "text-red-400" : "text-green-400"}`}>{money(r.variance)}</td>
                <td className={`px-3 py-2.5 text-right tabular-nums font-semibold ${r.off_track ? "text-red-400" : "text-[#c5a24a]"}`}>{r.percent_used}%</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div className="overflow-hidden rounded-xl border border-white/10 bg-[#1a1a1a]">
        <div className="flex items-center justify-between border-b border-white/10 px-5 py-3">
          <span className="text-sm font-semibold text-[#f7f3ec]">Time & Cost Entries</span>
          <button onClick={onAdd} data-testid="add-time-btn" className="flex items-center gap-1.5 rounded-lg border border-white/15 px-2.5 py-1.5 text-xs font-semibold text-neutral-300 hover:bg-white/5"><Plus className="h-3.5 w-3.5" /> Log Time</button>
        </div>
        <table className="w-full text-sm">
          <thead><tr className="border-b border-white/10 text-[11px] uppercase tracking-wider text-neutral-500"><th className="px-5 py-2 text-left font-semibold">Person</th><th className="px-3 py-2 text-left font-semibold">Area</th><th className="px-3 py-2 text-left font-semibold">Week</th><th className="px-3 py-2 text-right font-semibold">Hours</th><th className="px-3 py-2 text-right font-semibold">Rate</th><th className="px-3 py-2 text-right font-semibold">Amount</th><th className="px-2 py-2" /></tr></thead>
          <tbody>
            {time.length === 0 && <tr><td colSpan={7} className="px-5 py-5 text-center text-xs text-neutral-600">No time logged yet.</td></tr>}
            {time.map((t) => (
              <tr key={t.id} className="border-b border-white/5 last:border-0">
                <td className="px-5 py-2.5 text-neutral-200">{t.person}</td>
                <td className="px-3 py-2.5 text-neutral-400">{areaName(t.area_id)}</td>
                <td className="px-3 py-2.5 text-neutral-400">{t.week || "—"}</td>
                <td className="px-3 py-2.5 text-right tabular-nums text-neutral-300">{t.hours}</td>
                <td className="px-3 py-2.5 text-right tabular-nums text-neutral-300">{money(t.rate)}</td>
                <td className="px-3 py-2.5 text-right tabular-nums font-semibold text-[#c5a24a]">{money(t.amount)}</td>
                <td className="px-2 py-2.5 text-right"><button onClick={() => onDel(t.id)} className="rounded-md p-1.5 text-neutral-500 hover:bg-red-950/50 hover:text-red-400"><Trash2 className="h-3.5 w-3.5" /></button></td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <p className="text-xs italic text-neutral-600">Costs are internal only — never shown on the client share link.</p>
    </div>
  );
}

function TimeModal({ projectId, areas, team, onClose, onSaved }) {
  const [f, setF] = useState({ person: "", area_id: "", week: "", hours: "", rate: "", note: "" });
  const [saving, setSaving] = useState(false);
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });
  const pickPerson = (e) => {
    const m = (team || []).find((x) => x.name === e.target.value);
    setF({ ...f, person: e.target.value, rate: m && m.rate ? String(m.rate) : f.rate });
  };
  const submit = async (e) => {
    e.preventDefault(); setSaving(true);
    try {
      await api.post(`/projects/${projectId}/time`, { person: f.person, area_id: f.area_id, week: f.week, hours: parseFloat(f.hours) || 0, rate: parseFloat(f.rate) || 0, note: f.note });
      toast.success("Logged"); onSaved();
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); } finally { setSaving(false); }
  };
  return (
    <Modal title="Log Time" testid="time-modal">
      <form onSubmit={submit} className="space-y-4 px-6 py-5">
        <div className="grid grid-cols-2 gap-4">
          <div><label className={lbl}>Person</label><input data-testid="time-person-input" required list="team-names" className={field} value={f.person} onChange={pickPerson} placeholder="Peter Tulikowski" /><datalist id="team-names">{(team || []).map((m, i) => <option key={i} value={m.name} />)}</datalist></div>
          <div><label className={lbl}>Area</label><select className={field} value={f.area_id} onChange={set("area_id")}><option value="">— Unassigned —</option>{areas.map((a) => <option key={a.id} value={a.id}>{a.name}</option>)}</select></div>
        </div>
        <div className="grid grid-cols-3 gap-4">
          <div><label className={lbl}>Week</label><input className={field} value={f.week} onChange={set("week")} placeholder="01/01-01/07" /></div>
          <div><label className={lbl}>Hours</label><input data-testid="time-hours-input" type="number" step="0.5" className={field} value={f.hours} onChange={set("hours")} /></div>
          <div><label className={lbl}>Rate ($/hr)</label><input data-testid="time-rate-input" type="number" className={field} value={f.rate} onChange={set("rate")} /></div>
        </div>
        <div className="flex justify-end gap-3 pt-2">
          <button type="button" onClick={onClose} className={btnGhost}>Cancel</button>
          <button data-testid="time-save-btn" disabled={saving} type="submit" className={btnGold}>{saving && <Loader2 className="h-4 w-4 animate-spin" />} Save</button>
        </div>
      </form>
    </Modal>
  );
}

function ReportTab({ project, chart, pace, published, onEditNarrative, onPublish, projectId, navigate }) {
  const n = project.narrative || project.report || {};
  const sections = [
    ["Drawings Returned", n.drawings_returned], ["Outstanding Responses", n.outstanding_responses],
    ["Clarifications", n.clarifications], ["Risk to Cost & Schedule", n.risk_notes], ["Change Quotations", n.change_quotations],
  ];
  return (
    <div className="space-y-6">
      <div className="flex flex-wrap justify-end gap-2">
        <button onClick={onEditNarrative} data-testid="edit-narrative-btn" className={btnGhost + " flex items-center gap-2"}><Pencil className="h-4 w-4" /> Edit Report Notes</button>
        <button onClick={onPublish} data-testid="publish-btn" className={btnGhost + " flex items-center gap-2"}><FileText className="h-4 w-4" /> Publish Version</button>
        <a href={`/projects/${projectId}/report`} target="_blank" rel="noreferrer" data-testid="open-report-btn" className={btnGold}><ExternalLink className="h-4 w-4" /> Open / Print Report (PDF)</a>
      </div>

      <div className="rounded-xl border border-white/10 bg-[#1a1a1a] p-6">
        <div className="mb-4 text-xs font-semibold uppercase tracking-widest text-neutral-500">Week-over-Week Progress</div>
        {chart.length > 0 ? (
          <ResponsiveContainer width="100%" height={220}>
            <LineChart data={chart} margin={{ top: 5, right: 10, left: -20, bottom: 0 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#2a2a2a" /><XAxis dataKey="week" stroke="#737373" fontSize={11} tickLine={false} /><YAxis stroke="#737373" fontSize={11} tickLine={false} domain={[0, 100]} />
              <Tooltip contentStyle={{ background: "#1a1a1a", border: "1px solid #333", borderRadius: 8, fontSize: 12 }} labelStyle={{ color: "#a3a3a3" }} />
              <Line type="monotone" dataKey="progress" stroke={GOLD} strokeWidth={2.5} dot={{ r: 3, fill: GOLD }} name="Progress %" />
            </LineChart>
          </ResponsiveContainer>
        ) : <div className="flex h-[220px] items-center justify-center text-sm text-neutral-600">Snapshots build as the project progresses.</div>}
      </div>

      <div className="rounded-xl border border-white/10 bg-[#1a1a1a] p-6">
        <div className="mb-1 text-xs font-semibold uppercase tracking-widest text-neutral-500">Pace History — Drawings Completed / Week</div>
        <p className="mb-3 text-xs text-neutral-600">See if the team is speeding up or slipping week to week.</p>
        {pace && pace.length > 0 ? (
          <ResponsiveContainer width="100%" height={220}>
            <BarChart data={pace} margin={{ top: 5, right: 10, left: -20, bottom: 0 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#2a2a2a" /><XAxis dataKey="week" stroke="#737373" fontSize={11} tickLine={false} /><YAxis stroke="#737373" fontSize={11} tickLine={false} allowDecimals={false} />
              <Tooltip cursor={{ fill: "rgba(197,162,74,0.08)" }} contentStyle={{ background: "#1a1a1a", border: "1px solid #333", borderRadius: 8, fontSize: 12 }} labelStyle={{ color: "#a3a3a3" }} />
              <Bar dataKey="pace" fill={GOLD} radius={[3, 3, 0, 0]} name="Drawings / week" />
            </BarChart>
          </ResponsiveContainer>
        ) : <div className="flex h-[220px] items-center justify-center text-sm text-neutral-600">Pace shows once weekly snapshots accumulate.</div>}
      </div>

      <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
        {sections.map(([title, items]) => (
          <div key={title} className="rounded-xl border border-white/10 bg-[#1a1a1a] p-5">
            <div className="mb-2 text-xs font-semibold uppercase tracking-widest text-neutral-500">{title}</div>
            {items && items.length ? <ul className="list-disc space-y-1 pl-5 text-sm text-neutral-300">{items.map((x, i) => <li key={i}>{x}</li>)}</ul> : <p className="text-sm text-neutral-600">N/A</p>}
          </div>
        ))}
      </div>

      <div className="rounded-xl border border-white/10 bg-[#1a1a1a] p-5">
        <div className="mb-3 text-xs font-semibold uppercase tracking-widest text-neutral-500">Weekly Reports</div>
        {(published || []).length === 0 ? <p className="text-sm text-neutral-600">Publish a version to create a downloadable weekly report.</p> : (
          <div className="divide-y divide-white/5">
            {published.map((r) => (
              <div key={r.id} data-testid={`weekly-report-${r.id}`} className="flex items-center justify-between py-2.5">
                <div><span className="font-semibold text-[#c5a24a]">{r.version}</span><span className="ml-3 text-sm text-neutral-300">{r.description}</span><span className="ml-3 text-xs text-neutral-500">{r.date} · {r.overall_percent}%</span></div>
                <a href={`/reports/view/${r.id}`} target="_blank" rel="noreferrer" className="flex items-center gap-1.5 rounded-lg border border-white/15 px-3 py-1.5 text-xs font-semibold text-neutral-300 hover:bg-white/5"><ExternalLink className="h-3.5 w-3.5" /> Open / Download</a>
              </div>
            ))}
          </div>
        )}
      </div>

      <div className="rounded-xl border border-white/10 bg-[#1a1a1a] p-5">
        <div className="mb-3 text-xs font-semibold uppercase tracking-widest text-neutral-500">Document Control — Revision History</div>
        {(project.revision_history || []).length === 0 ? <p className="text-sm text-neutral-600">No versions published yet.</p> : (
          <table className="w-full text-sm"><thead><tr className="text-[11px] uppercase tracking-wider text-neutral-500"><th className="py-1 text-left font-semibold">Date</th><th className="py-1 text-left font-semibold">Version</th><th className="py-1 text-left font-semibold">Description</th><th className="py-1 text-left font-semibold">Author</th></tr></thead>
          <tbody>{project.revision_history.map((r, i) => <tr key={i} className="border-t border-white/5"><td className="py-1.5 text-neutral-400">{r.date}</td><td className="py-1.5 font-semibold text-[#c5a24a]">{r.version}</td><td className="py-1.5 text-neutral-300">{r.description}</td><td className="py-1.5 text-neutral-400">{r.author}</td></tr>)}</tbody></table>
        )}
      </div>
    </div>
  );
}

function ProjectModal({ project, onClose, onSaved }) {
  const [f, setF] = useState({
    name: project.name, job_number: project.job_number, client_name: project.client_name, status: project.status,
    project_manager: project.project_manager || "", project_sponsor: project.project_sponsor || "", scope_total: project.scope_total || "",
    client_contact_name: project.client_contact_name || "", client_contact_phone: project.client_contact_phone || "", client_contact_email: project.client_contact_email || "", client_address: project.client_address || "",
  });
  const [team, setTeam] = useState(project.team || []);
  const [saving, setSaving] = useState(false);
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });
  const setTM = (i, k, v) => setTeam(team.map((m, j) => j === i ? { ...m, [k]: v } : m));
  const submit = async (e) => {
    e.preventDefault(); setSaving(true);
    try {
      await api.put(`/projects/${project.id}`, { ...f, scope_total: parseInt(f.scope_total) || 0, team: team.map((m) => ({ name: m.name, role: m.role || "", phone: m.phone || "", rate: parseFloat(m.rate) || 0 })) });
      toast.success("Saved"); onSaved();
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); } finally { setSaving(false); }
  };
  return (
    <Modal title="Edit Project" testid="edit-job-modal" wide>
      <form onSubmit={submit} className="space-y-4 px-6 py-5">
        <div className="grid grid-cols-2 gap-4">
          <div><label className={lbl}>Project Name</label><input required className={field} value={f.name} onChange={set("name")} /></div>
          <div><label className={lbl}>Job Number</label><input required className={field} value={f.job_number} onChange={set("job_number")} /></div>
        </div>
        <div className="grid grid-cols-3 gap-4">
          <div><label className={lbl}>Client</label><input required className={field} value={f.client_name} onChange={set("client_name")} /></div>
          <div><label className={lbl}>Project Manager</label><input className={field} value={f.project_manager} onChange={set("project_manager")} /></div>
          <div><label className={lbl}>Status</label><select data-testid="job-status-select" className={field} value={f.status} onChange={set("status")}><option value="active">Active</option><option value="on_hold">On Hold</option><option value="completed">Completed</option></select></div>
        </div>
        <div className="grid grid-cols-2 gap-4">
          <div><label className={lbl}>Project Sponsor</label><input className={field} value={f.project_sponsor} onChange={set("project_sponsor")} /></div>
          <div><label className={lbl}>Total Scope (dwgs)</label><input type="number" className={field} value={f.scope_total} onChange={set("scope_total")} /></div>
        </div>
        <div className="rounded-lg border border-white/10 p-3">
          <div className="mb-2 text-xs font-semibold uppercase tracking-wide text-neutral-400">Client Contact</div>
          <div className="grid grid-cols-2 gap-3">
            <input className={field} value={f.client_contact_name} onChange={set("client_contact_name")} placeholder="Contact name" />
            <input className={field} value={f.client_contact_phone} onChange={set("client_contact_phone")} placeholder="Phone" />
            <input className={field} value={f.client_contact_email} onChange={set("client_contact_email")} placeholder="Email" />
            <input className={field} value={f.client_address} onChange={set("client_address")} placeholder="Address" />
          </div>
        </div>
        <div className="rounded-lg border border-white/10 p-3">
          <div className="mb-2 flex items-center justify-between"><span className="text-xs font-semibold uppercase tracking-wide text-neutral-400">Project Team / Org Chart (rates internal)</span><button type="button" onClick={() => setTeam([...team, { name: "", role: "", phone: "", rate: "" }])} className="text-xs font-semibold text-[#c5a24a]">+ Add</button></div>
          <div className="space-y-2">
            {team.map((m, i) => (
              <div key={i} className="grid grid-cols-12 gap-2">
                <input className={field + " col-span-4"} value={m.name} onChange={(e) => setTM(i, "name", e.target.value)} placeholder="Name" />
                <input className={field + " col-span-3"} value={m.role} onChange={(e) => setTM(i, "role", e.target.value)} placeholder="Role" />
                <input className={field + " col-span-3"} value={m.phone} onChange={(e) => setTM(i, "phone", e.target.value)} placeholder="Phone" />
                <input className={field + " col-span-1"} type="number" value={m.rate} onChange={(e) => setTM(i, "rate", e.target.value)} placeholder="$/hr" />
                <button type="button" onClick={() => setTeam(team.filter((_, j) => j !== i))} className="col-span-1 rounded-md text-neutral-500 hover:text-red-400"><Trash2 className="mx-auto h-4 w-4" /></button>
              </div>
            ))}
          </div>
        </div>
        <div className="flex justify-end gap-3 pt-2">
          <button type="button" onClick={onClose} className={btnGhost}>Cancel</button>
          <button data-testid="job-update-submit" disabled={saving} type="submit" className={btnGold}>{saving && <Loader2 className="h-4 w-4 animate-spin" />} Save</button>
        </div>
      </form>
    </Modal>
  );
}

function NarrativeModal({ project, onClose, onSaved }) {
  const n = project.narrative || project.report || {};
  const toText = (a) => (a || []).join("\n");
  const [f, setF] = useState({
    drawings_returned: toText(n.drawings_returned), outstanding_responses: toText(n.outstanding_responses),
    clarifications: toText(n.clarifications), risk_notes: toText(n.risk_notes), change_quotations: toText(n.change_quotations),
  });
  const [saving, setSaving] = useState(false);
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });
  const toList = (s) => s.split("\n").map((x) => x.trim()).filter(Boolean);
  const submit = async (e) => {
    e.preventDefault(); setSaving(true);
    try {
      await api.put(`/projects/${project.id}`, { report: { drawings_returned: toList(f.drawings_returned), outstanding_responses: toList(f.outstanding_responses), clarifications: toList(f.clarifications), risk_notes: toList(f.risk_notes), change_quotations: toList(f.change_quotations) } });
      toast.success("Saved"); onSaved();
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); } finally { setSaving(false); }
  };
  const flds = [["drawings_returned", "Drawings Returned"], ["outstanding_responses", "Outstanding Responses"], ["clarifications", "Clarifications"], ["risk_notes", "Risk to Cost & Schedule"], ["change_quotations", "Change Quotations"]];
  return (
    <Modal title="Edit Report Notes" testid="narrative-modal" wide>
      <form onSubmit={submit} className="space-y-4 px-6 py-5">
        <p className="text-xs text-neutral-500">One item per line — each becomes a bullet in the report.</p>
        {flds.map(([k, label]) => <div key={k}><label className={lbl}>{label}</label><textarea className={`${field} min-h-[60px] resize-none`} value={f[k]} onChange={set(k)} /></div>)}
        <div className="flex justify-end gap-3 pt-2"><button type="button" onClick={onClose} className={btnGhost}>Cancel</button><button data-testid="narrative-save-btn" disabled={saving} type="submit" className={btnGold}>{saving && <Loader2 className="h-4 w-4 animate-spin" />} Save</button></div>
      </form>
    </Modal>
  );
}

function PublishModal({ project, onClose, onSaved }) {
  const [desc, setDesc] = useState("");
  const [saving, setSaving] = useState(false);
  const submit = async (e) => {
    e.preventDefault(); setSaving(true);
    try { await api.post(`/projects/${project.id}/publish`, { description: desc }); toast.success("Report version published"); onSaved(); }
    catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); } finally { setSaving(false); }
  };
  return (
    <Modal title="Publish Report Version" testid="publish-modal">
      <form onSubmit={submit} className="space-y-4 px-6 py-5">
        <p className="text-sm text-neutral-500">Adds a dated entry to Document Control and snapshots current progress.</p>
        <div><label className={lbl}>Description</label><input data-testid="publish-desc-input" required className={field} value={desc} onChange={(e) => setDesc(e.target.value)} placeholder="Jan 1 - Jan 7, 2024 Progress" /></div>
        <div className="flex justify-end gap-3 pt-2"><button type="button" onClick={onClose} className={btnGhost}>Cancel</button><button data-testid="publish-save-btn" disabled={saving} type="submit" className={btnGold}>{saving && <Loader2 className="h-4 w-4 animate-spin" />} Publish</button></div>
      </form>
    </Modal>
  );
}

function ImportDrawingsModal({ projectId, areas, onClose, onSaved }) {
  const [fileObj, setFileObj] = useState(null);
  const [preview, setPreview] = useState(null); // {headers, sample, header_row}
  const [mapping, setMapping] = useState({});
  const [areaId, setAreaId] = useState("");
  const [newArea, setNewArea] = useState("");
  const [busy, setBusy] = useState(false);

  const guess = (headers) => {
    const map = {};
    const norm = (h) => String(h).toLowerCase();
    IMPORT_FIELDS.forEach((f) => {
      const idx = headers.findIndex((h) => {
        const n = norm(h);
        if (f.key === "drawing_number") return n.includes("drawing") || n.includes("dwg") || n.includes("number") || n.includes("no.");
        if (f.key === "requires_attention") return n.includes("attention");
        if (f.key === "received_date") return n.includes("date");
        if (f.key === "received") return n === "received" || n.includes("rec'd") || n.includes("received qty");
        if (f.key === "title") return n === "title" || n.includes("drawing title") || n.includes("dwg title");
        if (f.key === "rev") return n === "rev" || n.includes("revision") || n === "rev.";
        if (f.key === "sheet") return n === "sheet" || n.includes("sheet no") || n.includes("sht");
        if (f.key === "discipline") return n.includes("discipline") || n === "disc";
        return n.includes(f.key.replace("_", " ")) || n.includes(f.label.toLowerCase());
      });
      if (idx >= 0) map[f.key] = String(idx);
    });
    return map;
  };

  const doPreview = async (fobj) => {
    setBusy(true);
    const fd = new FormData(); fd.append("file", fobj);
    try {
      const { data } = await api.post(`/projects/${projectId}/import/preview`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      setPreview(data); setMapping(guess(data.headers));
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); } finally { setBusy(false); }
  };

  const onFile = (e) => { const f = e.target.files[0]; setFileObj(f); if (f) doPreview(f); };

  const doImport = async () => {
    if (mapping.drawing_number === undefined || mapping.drawing_number === "") { toast.error("Map the Drawing # column"); return; }
    setBusy(true);
    const fd = new FormData();
    fd.append("file", fileObj);
    fd.append("mapping", JSON.stringify(mapping));
    fd.append("header_row", preview.header_row);
    fd.append("area_id", areaId);
    fd.append("new_area_name", newArea);
    try {
      const { data } = await api.post(`/projects/${projectId}/import/drawings`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success(`Imported ${data.packages_imported} drawings`);
      onSaved();
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); } finally { setBusy(false); }
  };

  return (
    <Modal title="Import Drawing List" testid="import-drawings-modal" wide>
      <div className="space-y-4 px-6 py-5">
        <p className="text-sm text-neutral-400">Upload the client's drawing register / list (.xlsx or .csv). Map their columns to our fields, then import. Optional — only if the client supplies one.</p>
        <label className="flex cursor-pointer flex-col items-center justify-center gap-2 rounded-lg border-2 border-dashed border-white/15 px-6 py-6 text-center hover:border-[#c5a24a]/50">
          <Upload className="h-6 w-6 text-[#c5a24a]" />
          <span className="text-sm text-neutral-300">{fileObj ? fileObj.name : "Click to choose an .xlsx or .csv file"}</span>
          <input data-testid="import-drawings-file" type="file" accept=".xlsx,.xlsm,.csv" className="hidden" onChange={onFile} />
        </label>

        {busy && !preview && <div className="flex justify-center py-4"><Loader2 className="h-5 w-5 animate-spin text-[#c5a24a]" /></div>}

        {preview && (
          <>
            <div className="rounded-lg border border-white/10 p-3">
              <div className="mb-2 text-xs font-semibold uppercase tracking-wide text-neutral-400">Map Columns (their column → our field)</div>
              <div className="grid grid-cols-2 gap-3">
                {IMPORT_FIELDS.map((f) => (
                  <div key={f.key}>
                    <label className="mb-1 block text-[11px] font-semibold text-neutral-400">{f.label}{f.required && <span className="text-red-400"> *</span>}</label>
                    <select data-testid={`map-${f.key}`} className={field} value={mapping[f.key] ?? ""} onChange={(e) => setMapping({ ...mapping, [f.key]: e.target.value })}>
                      <option value="">— none —</option>
                      {preview.headers.map((h, i) => <option key={i} value={i}>{h}</option>)}
                    </select>
                  </div>
                ))}
              </div>
            </div>
            {(mapping.area === undefined || mapping.area === "") && (
              <div className="rounded-lg border border-white/10 p-3">
                <div className="mb-2 text-xs font-semibold uppercase tracking-wide text-neutral-400">No area column mapped — where should these go?</div>
                <div className="grid grid-cols-2 gap-3">
                  <select className={field} value={areaId} onChange={(e) => setAreaId(e.target.value)}>
                    <option value="">— New area —</option>
                    {areas.map((a) => <option key={a.id} value={a.id}>{a.name}</option>)}
                  </select>
                  {!areaId && <input className={field} value={newArea} onChange={(e) => setNewArea(e.target.value)} placeholder="New area name (e.g. Client Upload)" />}
                </div>
              </div>
            )}
            <p className="text-xs text-neutral-600">Tip: if you don't map a "Received (count)" column, each row counts as 1 received drawing.</p>
          </>
        )}

        <div className="flex justify-end gap-3 pt-1">
          <button type="button" onClick={onClose} className={btnGhost}>Cancel</button>
          <button onClick={doImport} disabled={!preview || busy} data-testid="import-drawings-submit" className={btnGold}>{busy && <Loader2 className="h-4 w-4 animate-spin" />} Import Drawings</button>
        </div>
      </div>
    </Modal>
  );
}

function ShareModal({ project, onClose, onSaved }) {
  const [passcode, setPasscode] = useState(project.share_passcode || "");
  const [enabled, setEnabled] = useState(project.share_enabled);
  const [saving, setSaving] = useState(false);
  const [copied, setCopied] = useState(false);
  const shareUrl = `${window.location.origin}/share/${project.share_token}`;
  const enable = async () => {
    if (!passcode) { toast.error("Set a passcode first"); return; }
    setSaving(true);
    try { await api.post(`/projects/${project.id}/share`, { passcode }); setEnabled(true); toast.success("Sharing enabled"); onSaved(); }
    catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); } finally { setSaving(false); }
  };
  const disable = async () => { await api.post(`/projects/${project.id}/share/disable`); setEnabled(false); toast.success("Sharing disabled"); onSaved(); };
  const copy = () => { navigator.clipboard.writeText(shareUrl); setCopied(true); setTimeout(() => setCopied(false), 1500); };
  return (
    <Modal title="Client Share Link" testid="share-modal">
      <div className="space-y-4 px-6 py-5">
        <p className="-mt-2 text-sm text-neutral-500">Read-only progress report, passcode protected. Costs are never shown.</p>
        <div><label className={lbl}>Access Passcode</label><input data-testid="share-passcode-input" className={field} value={passcode} onChange={(e) => setPasscode(e.target.value)} placeholder="e.g. KEYERA-2024" /></div>
        {enabled ? (
          <>
            <div><label className={lbl}>Shareable URL</label><div className="flex gap-2"><input readOnly data-testid="share-url" value={shareUrl} className={`${field} text-xs`} /><button onClick={copy} data-testid="copy-share-url" className="flex items-center rounded-lg border border-white/15 px-3 text-sm text-neutral-300 hover:bg-white/5">{copied ? <Check className="h-4 w-4 text-green-400" /> : <Copy className="h-4 w-4" />}</button></div><p className="mt-2 text-xs text-neutral-500">Email this link and passcode <span className="font-semibold text-[#c5a24a]">{passcode}</span> to your client.</p></div>
            <div className="flex justify-between gap-3 pt-2"><button onClick={disable} data-testid="disable-share-btn" className="rounded-lg border border-red-900/50 px-4 py-2 text-sm font-medium text-red-400 hover:bg-red-950/40">Disable Sharing</button><button onClick={enable} disabled={saving} className={btnGold}>Update Passcode</button></div>
          </>
        ) : (
          <div className="flex justify-end gap-3 pt-2"><button type="button" onClick={onClose} className={btnGhost}>Cancel</button><button onClick={enable} data-testid="enable-share-btn" disabled={saving} className={btnGold}>{saving && <Loader2 className="h-4 w-4 animate-spin" />} Enable Sharing</button></div>
        )}
      </div>
    </Modal>
  );
}
